import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Instagram, Facebook, ArrowUp, Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import cpLogo from "@/assets/cp-logo.png";
import { NewsletterForm } from "@/components/NewsletterForm";

interface StoreInfo {
  phone?: string;
  email?: string;
  address?: string;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
}

export const Footer = () => {
  const { t } = useTranslation();
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const [storeInfo, setStoreInfo] = useState<StoreInfo>({});

  useEffect(() => {
    supabase
      .from("store_settings")
      .select("value")
      .eq("key", "store_info")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) setStoreInfo(data.value as unknown as StoreInfo);
      });
  }, []);

  const fbUrl = storeInfo.facebook
    ? `https://www.facebook.com/${storeInfo.facebook}`
    : "https://www.facebook.com/";
  const igUrl = storeInfo.instagram
    ? `https://www.instagram.com/${storeInfo.instagram}`
    : "https://www.instagram.com/";
  const ttUrl = storeInfo.tiktok
    ? `https://www.tiktok.com/@${storeInfo.tiktok}`
    : "https://www.tiktok.com/";

  return (
    <footer className="bg-foreground text-background">
      {/* Newsletter Section */}
      <div className="border-b border-background/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 rounded-2xl bg-background/10 flex items-center justify-center mx-auto mb-6">
              <Bell className="w-8 h-8 text-background" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-background mb-3">
              {t("sections.stayUpdated")}
            </h2>
            <p className="text-background/70 mb-8 max-w-md mx-auto">
              {t("sections.subscribeText")}
            </p>
            <div className="flex justify-center">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-8">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
          <div className="col-span-2 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src={cpLogo} alt="Chawni Phone" className="w-11 h-11 rounded-xl object-contain" width={44} height={44} />
              <span className="text-lg font-display font-bold">Chawni Phone</span>
            </div>
            <p className="text-sm opacity-60 leading-relaxed max-w-xs">{t("footer.tagline")}</p>
            <div className="flex gap-3 mt-5">
              <a href={fbUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-background/10 hover:bg-background/20 flex items-center justify-center transition-colors" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
              <a href={igUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-background/10 hover:bg-background/20 flex items-center justify-center transition-colors" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
              <a href={ttUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-background/10 hover:bg-background/20 flex items-center justify-center transition-colors" aria-label="TikTok">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.72a8.2 8.2 0 0 0 4.76 1.52V6.79a4.84 4.84 0 0 1-1-.1z"/></svg>
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-display font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">{t("footer.quickLinks")}</h4>
            <div className="space-y-3 text-sm">
              <Link to="/shop" className="block opacity-60 hover:opacity-100 transition-opacity">{t("nav.shop")}</Link>
              <Link to="/sell" className="block opacity-60 hover:opacity-100 transition-opacity">{t("nav.sell")}</Link>
              <Link to="/compare" className="block opacity-60 hover:opacity-100 transition-opacity">{t("nav.compare")}</Link>
              <Link to="/marketplace" className="block opacity-60 hover:opacity-100 transition-opacity">{t("nav.marketplace")}</Link>
              <Link to="/repair" className="block opacity-60 hover:opacity-100 transition-opacity">{t("nav.repair")}</Link>
              <Link to="/giveaways" className="block opacity-60 hover:opacity-100 transition-opacity">{t("nav.giveaways")}</Link>
              <Link to="/recommend" className="block opacity-60 hover:opacity-100 transition-opacity">{t("nav.recommend")}</Link>
            </div>
          </div>
          <div>
            <h4 className="font-display font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">{t("footer.categories")}</h4>
            <div className="space-y-3 text-sm">
              <Link to="/shop?brand=Apple" className="block opacity-60 hover:opacity-100 transition-opacity">{t("brands.iphone")}</Link>
              <Link to="/shop?brand=Samsung" className="block opacity-60 hover:opacity-100 transition-opacity">{t("brands.samsung")}</Link>
              <Link to="/shop?brand=Xiaomi" className="block opacity-60 hover:opacity-100 transition-opacity">{t("brands.xiaomi")}</Link>
              <Link to="/shop?brand=OnePlus" className="block opacity-60 hover:opacity-100 transition-opacity">{t("brands.oneplus")}</Link>
              <Link to="/shop?brand=Google" className="block opacity-60 hover:opacity-100 transition-opacity">{t("brands.pixel")}</Link>
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <h4 className="font-display font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">{t("footer.contact")}</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3 opacity-60"><MapPin className="w-4 h-4 shrink-0 mt-0.5" /><span>{storeInfo.address || "Tangier, Morocco"}</span></div>
              <a href={`tel:${storeInfo.phone || "+212647424567"}`} className="flex items-center gap-3 opacity-60 hover:opacity-100 transition-opacity"><Phone className="w-4 h-4 shrink-0" /><span>{storeInfo.phone || "+212 647 424 567"}</span></a>
              <a href={`mailto:${storeInfo.email || "info@chawniphone.com"}`} className="flex items-center gap-3 opacity-60 hover:opacity-100 transition-opacity"><Mail className="w-4 h-4 shrink-0" /><span>{storeInfo.email || "info@chawniphone.com"}</span></a>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-background/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 sm:grid-cols-3 items-center gap-4 sm:gap-0">
          <div className="flex justify-center sm:justify-start order-2 sm:order-1">
            <p className="text-xs opacity-50 text-center sm:text-left">{t("footer.copyright", { year: new Date().getFullYear() })}</p>
          </div>
          
          <div className="flex justify-center order-1 sm:order-2">
            <p className="text-xs opacity-70 flex items-center gap-1">
              {t("footer.builtBy")} 
              <span className="font-bold text-white opacity-100 flex items-center gap-1">
                 HyGen Labs.
              </span>
            </p>
          </div>

          <div className="flex justify-center sm:justify-end order-3">
            <button 
              onClick={scrollToTop} 
              className="w-9 h-9 rounded-full bg-background/10 hover:bg-background/20 flex items-center justify-center transition-colors group" 
              aria-label="Scroll to top"
            >
              <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
